/**
 * Business landing — executive quick-access cards.
 *
 * Uses the premium card system: soft shadows, 16px radius, colored icon
 * halos, arrow reveal on hover.
 */
import { Link } from "react-router-dom";
import {
  Crown, Gauge, PiggyBank, Map as MapIcon, Users2, LineChart,
  ArrowRight,
} from "lucide-react";

const CARDS = [
  { to: "/v3/executive",   label: "Executive Dashboard",
    desc: "Company-wide KPIs, revenue attainment, pipeline coverage.",
    icon: Crown,   accent: "#F59E0B", testid: "v3-business-card-executive" },
  { to: "/v3/kpi",         label: "KPI Explorer",
    desc: "Drill into every metric in the catalog, filter by team and window.",
    icon: Gauge,   accent: "#3B82F6", testid: "v3-business-card-kpi" },
  { to: "/v3/portfolio",   label: "Portfolio Intelligence",
    desc: "Cash-flow, equity and cap-rate across your owned properties.",
    icon: PiggyBank, accent: "#10B981", testid: "v3-business-card-portfolio" },
  { to: "/v3/market",      label: "Market Intelligence",
    desc: "Live market signals — DOM, inventory, rent growth by metro.",
    icon: MapIcon, accent: "#8B5CF6", testid: "v3-business-card-market" },
  { to: "/v3/buyers",      label: "Buyer Intelligence",
    desc: "Cash-buyer directory with match scoring and outreach heat.",
    icon: Users2,  accent: "#EF4444", testid: "v3-business-card-buyers" },
  { to: "/v3/acquisition", label: "Acquisition Scorecards",
    desc: "Manager-level performance across sourcing, offer, and close.",
    icon: LineChart, accent: "#0EA5E9", testid: "v3-business-card-acquisition" },
];

export default function BusinessLanding() {
  return (
    <div data-testid="v3-business-landing" className="max-w-6xl">
      <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
        Business
      </div>
      <h1 className="mt-2 text-[32px] md:text-[38px] font-bold text-slate-900 tracking-tight leading-tight">
        Business Operations
      </h1>
      <p className="mt-3 text-slate-600 max-w-2xl text-[15px] leading-relaxed">
        Command-center view of company performance — pipeline, portfolio,
        market, and team execution in one place.
      </p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            data-testid={c.testid}
            className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
          >
            <div
              className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-[0.10] pointer-events-none"
              style={{ backgroundColor: c.accent }}
            />
            <div className="flex items-start gap-3 relative">
              <div className="h-11 w-11 rounded-xl grid place-items-center text-white shadow-md shrink-0"
                   style={{ backgroundImage: `linear-gradient(135deg, ${c.accent}, ${c.accent}cc)` }}>
                <c.icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[14px] font-bold text-slate-900 group-hover:text-slate-950">
                  {c.label}
                </div>
                <div className="mt-1 text-[12px] text-slate-500 leading-relaxed">
                  {c.desc}
                </div>
              </div>
            </div>
            <div className="mt-5 text-[12px] font-semibold inline-flex items-center gap-1"
                 style={{ color: c.accent }}>
              Open <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
